import h from 'hyperscript';

import type { LabelWithIcon } from 'ui/label-with-icon/label-with-icon';

import { label } from 'cadenza/utils/aria';
import { assert } from 'cadenza/utils/custom-error';
import { on } from 'cadenza/utils/event-util';
import type { Icon } from 'cadenza/utils/icon/icon';
import { isIcon } from 'cadenza/utils/icon/icon';
import { uniqueId } from 'cadenza/utils/unique-id';
import { logger } from 'cadenza/utils/logging';

import './segmented-control.css';

const COMPONENT_NAME = 'd-segmented-control';
const SEGMENTED_CONTROL_VALUE_CHANGED = 'change';

export interface Segment {
  /**
   * The value reported by the component when the user selects the segment.
   */
  value: string;
  /**
   * Whether the segment is disabled
   */
  disabled?: boolean;
  /** The displayed content of the segmented, by default the value. */
  content?: Icon | string | LabelWithIcon;
  /**
   * Icon of the segment.
   * @deprecated Use the `content` property instead.
   */
  icon?: Icon;
  /**
   * Label of the segment.
   * @deprecated Use the `content` property instead.
   */
  label?: string | LabelWithIcon;
}

/**
 * One of ariaLabel and ariaLabelledBy are mandatory.
 */
export interface SegmentedControlOptions<T extends string = string> {
  /**
   * Text to be used as aria-label value of the component.
   */
  ariaLabel?: string;
  /**
   * Id of the element used as aria-labelledby value of the component.
   */
  ariaLabelledBy?: string;
  /**
   * Name of the generated input (randomly generated if not provided).
   */
  name?: string;
  /**
   * The initial value
   */
  initialValue?: T;
  /**
   * Direction used to render the elements.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * if true, it is possible to have no segment selected by clicking on the selected segment
   */
  deselectable?: false;
}

/** @deprecated The use of separators in a segmented control is not allowed (see {@link https://jira.disy.net/browse/CADENZA-36374|CADENZA-36374}). */
type Separator = '---';
const SEPARATOR: Separator = '---';

export class SegmentedControl<T extends string = string> extends HTMLElement {

  readonly #initialValue?: T;

  /**
   * @param segments - The segments the user can select between. At least one segment must be provided, otherwise the component will raise an error.
   * @param options - Configuration to customise the render.
   * @param options.ariaLabel
   * @param options.ariaLabelledBy
   * @param options.name
   * @param options.initialValue
   * @param options.orientation
   * @param options.deselectable
   */
  constructor (
    segments: (string | Segment | Separator)[],
    {
      ariaLabel,
      ariaLabelledBy,
      name = uniqueId(),
      initialValue,
      orientation,
      deselectable = false
    }: SegmentedControlOptions<T>) {
    assert(segments.length >= 1, 'There must be at least one segment');
    super();

    if (segments.some(segment => segment === SEPARATOR)) {
      logger.warn('The use of separators in a segmented control is deprecated.');
      this.classList.add('d-legacy-segmented-control');
    }

    this.classList.add(COMPONENT_NAME);
    this.setAttribute('role', 'radiogroup');
    label(this, { ariaLabel, ariaLabelledBy });

    this.#initialValue = initialValue;

    if (orientation === 'vertical') {
      this.classList.add('is-vertical');
    }

    const labelledRadios = segments.flatMap((segment, i) => {
      if (segment === SEPARATOR) {
        return h('div', {
          attrs: {
            role: 'separator',
            'aria-orientation': orientation === 'horizontal' ? 'horizontal' : 'vertical'
          }
        });
      }

      if (typeof (segment) === 'string') {
        segment = { value: segment };
      }

      return this.#createRadioWithLabelElements(segment, name, i, deselectable);
    });
    this.append(...labelledRadios);
    if (!this.#getCheckedInput()) {
      (this.firstElementChild as HTMLInputElement).checked = true;
    }

    on(this, SEGMENTED_CONTROL_VALUE_CHANGED, event => {
      if (event.target !== this) {
        event.stopImmediatePropagation();
        this.dispatchChange();
      }
    });
  }

  connectedCallback () {
    if (this.#initialValue) {
      this.value = this.#initialValue;
    }
  }

  dispatchChange () {
    this.dispatchEvent(new CustomEvent(SEGMENTED_CONTROL_VALUE_CHANGED, {
      detail: { value: this.value },
      bubbles: true
    }));
  }

  // empty string is a valid value here which is used by "attribute-presentation-modification-panel.js"
  set value (value) {
    // uncheck all checked first (without it there is a problem in FF that 'this.querySelector('input:checked')' returns 2 items)
    this.querySelectorAll('input')
      .forEach(input => {
        input.checked = false;
      });

    const input = this.#getInputForValue(value);
    if (input) {
      input.checked = true;
    }
  }

  get value () {
    const inputElement = this.#getCheckedInput();
    if (inputElement) {
      return inputElement.value as T;
    }
  }

  set disabled (value) {
    this.querySelectorAll('input').forEach(input => {
      input.disabled = value;
    });

    if (value) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
  }

  get disabled () {
    return this.getAttribute('aria-disabled') === 'true';
  }

  #createRadioWithLabelElements (
    segment: Segment,
    name: string,
    i: number,
    deselectable: boolean): [ HTMLInputElement, HTMLElement ] {
    const input = this.#createInput(segment, i, name);
    const label = this.#createLabel(segment, input);

    if (deselectable) {
      this.#makeInputDeselectable(input, label);
    }

    return [ input, label ];
  }

  #createLabel ({ icon, label, value, content = icon ?? label ?? value }: Segment, input: HTMLInputElement) {
    if (isIcon(content)) {
      assert(content.getAttribute('aria-label'), 'Segment icon must be labelled');
      if (label) {
        logger.debug('Both, segment label and icon given - Ignoring the label');
      }
    }
    return h<HTMLLabelElement>('label', { htmlFor: input.id }, h('.ellipsis', content));
  }

  #makeInputDeselectable (input: HTMLInputElement, labelElement: HTMLLabelElement | LabelWithIcon) {
    // The event listener has to be on the labelElement (not on the input), because there
    // input.checked still has the pre-click value.
    labelElement.onclick = e => {
      if (input.checked) {
        input.checked = false;
        this.dispatchChange();
        e.preventDefault(); // prevents input onclick event from being fired, which would set checked = true again
      }
    };
    input.onkeydown = e => {
      if (e.code === 'Space') {
        labelElement.click();
        e.preventDefault(); // In Chrome prevents input onclick event from being fired, which would set checked = true again
      }
    };
    // Prevents onclick event on key up in Firefox
    input.onkeyup = e => {
      if (e.code === 'Space') {
        e.preventDefault();
      }
    };
  }

  #createInput ({ value, disabled }: Segment, i: number, name: string): HTMLInputElement {
    return h('input.visuallyhidden', {
      type: 'radio',
      id: `${name}-${i}`,
      name,
      value,
      disabled,
      checked: (value === this.#initialValue)
    });
  }

  #getInputForValue (value?: T) {
    return this.querySelector<HTMLInputElement>(`input[value="${value}"]`);
  }

  #getCheckedInput () {
    return this.querySelector<HTMLInputElement>('input:checked') ?? undefined;
  }

}

customElements.define(COMPONENT_NAME, SegmentedControl);
